/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { CheckboxCard, CheckboxGroup, Typography2 } from '@coursera/cds-core';

import _t from 'i18n!nls/academic-integrity';

export type Features = {
  gradedItemLocking: boolean;
  restrictCourseraCoachAccess: boolean;
  restrictCourseContentAccess: boolean;
};

export const featureCategories = {
  gradedItemLocking: 'gradedItemLocking',
  restrictCourseraCoachAccess: 'restrictCourseraCoachAccess',
  restrictCourseContentAccess: 'restrictCourseContentAccess',
} as const;

export type Feature = keyof typeof featureCategories;

type Props = {
  data: Features;
  onChange: (feature: Feature, checked: boolean) => void;
  showSupportText?: boolean;
};

const styles = {
  actions: css`
    display: flex;
    flex-flow: row wrap;
    gap: 24px 32px;
  `,
  block: css`
    display: block;
  `,
};

const FeatureChooser = (props: Props) => {
  const { data, showSupportText } = props;
  const defaultValues = Object.keys(data).filter((key) => Boolean(data[key as keyof Features]));
  const onChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    switch (event.target.value) {
      case featureCategories.gradedItemLocking:
        props.onChange(featureCategories.gradedItemLocking, checked);
        break;
      case featureCategories.restrictCourseContentAccess:
        props.onChange(featureCategories.restrictCourseContentAccess, checked);
        break;
      case featureCategories.restrictCourseraCoachAccess:
        props.onChange(featureCategories.restrictCourseraCoachAccess, checked);
        break;
      default:
        throw new Error('Invalid feature selected');
    }
  };
  return (
    <CheckboxGroup
      defaultValue={defaultValues}
      onChange={onChange}
      aria-label={_t('Select integrity features')}
      css={styles.actions}
    >
      <CheckboxCard
        // @ts-expect-error remove once we have a fix for the CheckboxCard component label
        label={
          <Typography2 variant="subtitleMedium" component="span" color="inherit">
            {_t('Graded Item Locking')}
          </Typography2>
        }
        value={featureCategories.gradedItemLocking}
        supportText={
          <>
            {showSupportText && (
              <Typography2 variant="actionSecondary" component="span" css={styles.block} color="inherit">
                {_t('Addresses Low learning effort')}
              </Typography2>
            )}
            <Typography2 variant="bodySecondary" component="span" css={styles.block} color="inherit">
              {_t('Restricts access to graded assessments until prior course content is completed.')}
            </Typography2>
          </>
        }
      />
      <CheckboxCard
        // @ts-expect-error remove once we have a fix for the CheckboxCard component label
        label={
          <Typography2 variant="subtitleMedium" component="span" color="inherit">
            {_t('Restrict Coursera Coach access')}
          </Typography2>
        }
        value={featureCategories.restrictCourseraCoachAccess}
        supportText={
          <>
            {showSupportText && (
              <Typography2 variant="actionSecondary" component="span" css={styles.block} color="inherit">
                {_t('Addresses Unauthorized use of resources')}
              </Typography2>
            )}

            <Typography2 variant="bodySecondary" component="span" css={styles.block} color="inherit">
              {_t('Restricts access to Coursera’s AI powered chat while a graded assessment is in progress.')}
            </Typography2>
          </>
        }
      />
      <CheckboxCard
        // @ts-expect-error remove once we have a fix for the CheckboxCard component label
        label={
          <Typography2 variant="subtitleMedium" component="span" color="inherit">
            {_t('Restrict course content access')}
          </Typography2>
        }
        value={featureCategories.restrictCourseContentAccess}
        supportText={
          <>
            {showSupportText && (
              <Typography2 variant="actionSecondary" component="span" css={styles.block} color="inherit">
                {_t('Addresses Unauthorized use of resources')}
              </Typography2>
            )}
            <Typography2 variant="bodySecondary" component="span" css={styles.block} color="inherit">
              {_t('Restricts access to all other course items while a graded assessment is in progress..')}
            </Typography2>
          </>
        }
      />
    </CheckboxGroup>
  );
};
export default FeatureChooser;
